import JSZip from 'jszip';
import { jsPDF } from 'jspdf';
import { formatFirstStepAsOfDate } from '../services/firstStepShareHtml';

export type FirstStepDownloadFormat = 'pdf' | 'docx' | 'txt';

export const FIRST_STEP_DEFAULT_DOWNLOAD_FORMAT: FirstStepDownloadFormat = 'pdf';

function xmlEscape(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function downloadFilename(lastEditedAt: string | undefined, extension: string): string {
  const d = lastEditedAt ? new Date(lastEditedAt) : new Date();
  const datePart = Number.isNaN(d.getTime())
    ? new Date().toISOString().slice(0, 10)
    : d.toISOString().slice(0, 10);
  return `My First Step — ${datePart}.${extension}`;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function storyLinesToParagraphs(text: string): string {
  return text.split('\n').map((line) => {
    if (!line) return '<w:p/>';
    return `<w:p>
      <w:pPr><w:spacing w:line="360" w:lineRule="auto" w:after="120"/></w:pPr>
      <w:r>
        <w:rPr><w:rFonts w:ascii="Georgia" w:hAnsi="Georgia"/><w:sz w:val="24"/></w:rPr>
        <w:t xml:space="preserve">${xmlEscape(line)}</w:t>
      </w:r>
    </w:p>`;
  }).join('\n');
}

async function buildFirstStepDocxBlob(contentText: string, lastEditedAt?: string): Promise<Blob> {
  const asOf = formatFirstStepAsOfDate(lastEditedAt);
  const bodyParts = [
    `<w:p>
      <w:pPr><w:spacing w:after="120"/></w:pPr>
      <w:r>
        <w:rPr><w:rFonts w:ascii="Georgia" w:hAnsi="Georgia"/><w:b/><w:sz w:val="32"/></w:rPr>
        <w:t>My First Step</w:t>
      </w:r>
    </w:p>`,
  ];
  if (asOf) {
    bodyParts.push(`<w:p>
      <w:pPr><w:spacing w:after="360"/></w:pPr>
      <w:r>
        <w:rPr><w:rFonts w:ascii="Georgia" w:hAnsi="Georgia"/><w:i/><w:color w:val="555555"/><w:sz w:val="22"/></w:rPr>
        <w:t>As of ${xmlEscape(asOf)}</w:t>
      </w:r>
    </w:p>`);
  }
  bodyParts.push(storyLinesToParagraphs(contentText.trim()));

  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>
    ${bodyParts.join('\n')}
    <w:sectPr>
      <w:pgSz w:w="12240" w:h="15840"/>
      <w:pgMar w:top="1440" w:right="1260" w:bottom="1440" w:left="1260"/>
    </w:sectPr>
  </w:body>
</w:document>`;

  const zip = new JSZip();
  zip.file(
    '[Content_Types].xml',
    `<?xml version="1.0" encoding="UTF-8"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
</Types>`
  );
  zip.file(
    '_rels/.rels',
    `<?xml version="1.0" encoding="UTF-8"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`
  );
  zip.file('word/document.xml', documentXml);
  zip.file(
    'word/_rels/document.xml.rels',
    `<?xml version="1.0" encoding="UTF-8"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"></Relationships>`
  );

  return zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
}

export function downloadFirstStepAsTxt(contentText: string, lastEditedAt?: string): void {
  const asOf = formatFirstStepAsOfDate(lastEditedAt);
  const lines = ['My First Step'];
  if (asOf) lines.push(`As of ${asOf}`);
  lines.push('', contentText.trim());
  const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
  triggerBlobDownload(blob, downloadFilename(lastEditedAt, 'txt'));
}

export async function downloadFirstStepAsDocx(
  contentText: string,
  lastEditedAt?: string
): Promise<void> {
  const blob = await buildFirstStepDocxBlob(contentText, lastEditedAt);
  triggerBlobDownload(blob, downloadFilename(lastEditedAt, 'docx'));
}

const PDF_MARGIN_X = 50;
const PDF_MARGIN_TOP = 54;
const PDF_MARGIN_BOTTOM = 54;
const PDF_BODY_FONT_SIZE = 12;
const PDF_BODY_LINE_HEIGHT = 17;

export function downloadFirstStepAsPdf(contentText: string, lastEditedAt?: string): void {
  const asOf = formatFirstStepAsOfDate(lastEditedAt);
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxWidth = pageWidth - PDF_MARGIN_X * 2;

  let y = PDF_MARGIN_TOP;

  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - PDF_MARGIN_BOTTOM) {
      doc.addPage();
      y = PDF_MARGIN_TOP;
    }
  };

  doc.setFont('times', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(26, 26, 26);
  doc.text('My First Step', PDF_MARGIN_X, y);
  y += 26;

  if (asOf) {
    ensureSpace(22);
    doc.setFont('times', 'italic');
    doc.setFontSize(11);
    doc.setTextColor(85, 85, 85);
    doc.text(`As of ${asOf}`, PDF_MARGIN_X, y);
    y += 30;
  }

  doc.setFont('times', 'normal');
  doc.setFontSize(PDF_BODY_FONT_SIZE);
  doc.setTextColor(26, 26, 26);

  const paragraphs = contentText.trim().split('\n');
  for (const paragraph of paragraphs) {
    if (!paragraph.trim()) {
      ensureSpace(PDF_BODY_LINE_HEIGHT * 0.6);
      y += PDF_BODY_LINE_HEIGHT * 0.6;
      continue;
    }
    const lines = doc.splitTextToSize(paragraph, maxWidth);
    for (const line of lines) {
      ensureSpace(PDF_BODY_LINE_HEIGHT);
      doc.text(line, PDF_MARGIN_X, y);
      y += PDF_BODY_LINE_HEIGHT;
    }
  }

  triggerBlobDownload(
    doc.output('blob'),
    downloadFilename(lastEditedAt, 'pdf')
  );
}

export async function downloadFirstStepStory(
  format: FirstStepDownloadFormat = FIRST_STEP_DEFAULT_DOWNLOAD_FORMAT,
  contentText: string,
  lastEditedAt?: string
): Promise<void> {
  if (format === 'txt') {
    downloadFirstStepAsTxt(contentText, lastEditedAt);
    return;
  }
  if (format === 'pdf') {
    downloadFirstStepAsPdf(contentText, lastEditedAt);
    return;
  }
  await downloadFirstStepAsDocx(contentText, lastEditedAt);
}
